import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
  inject,
  input,
} from '@angular/core';

@Directive({ selector: '[appImageZoomMagnifier]' })
export class ImageZoomMagnifierDirective implements AfterViewInit, OnDestroy, OnChanges {
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);

  readonly imgSrc = input<string>(undefined);
  readonly zoomRatio = input(4); // Default zoom ratio
  readonly zoomWindowElementRef = input<HTMLDivElement>(undefined);

  private zoomWindowContainerElementRef: HTMLElement;
  private lensElementRef: HTMLElement;
  private imageElement: HTMLImageElement;
  private resizeObserver: ResizeObserver;

  ngAfterViewInit() {
    this.createZoomElements();
    this.updateLensAndZoomWindowSize();
    this.resizeObserver = new ResizeObserver(() => this.updateLensAndZoomWindowSize());
    if (this.imageElement) {
      this.resizeObserver.observe(this.imageElement);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.imgSrc && this.imgSrc()) {
      this.updateZoomWindowImage();
    }
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    const rect = this.imageElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Ensure lens stays within image bounds
    const lensWidth = parseFloat(this.lensElementRef.style.width);
    const lensHeight = parseFloat(this.lensElementRef.style.height);
    const lensX = Math.max(0, Math.min(x - lensWidth / 2, rect.width - lensWidth));
    const lensY = Math.max(0, Math.min(y - lensHeight / 2, rect.height - lensHeight));

    // Calculate background position for the zoom window based on the lens position
    const backgroundPositionX = `${-lensX * this.zoomRatio()}px`;
    const backgroundPositionY = `${-lensY * this.zoomRatio()}px`;

    // Update lens and zoom window styles
    this.renderer.setStyle(this.lensElementRef, 'top', `${lensY}px`);
    this.renderer.setStyle(this.lensElementRef, 'left', `${lensX}px`);

    this.renderer.setStyle(this.zoomWindowContainerElementRef, 'backgroundImage', `url(${this.imgSrc()})`);
    this.renderer.setStyle(
      this.zoomWindowContainerElementRef,
      'backgroundSize',
      `${rect.width * this.zoomRatio()}px ${rect.height * this.zoomRatio()}px`
    );
    this.renderer.setStyle(
      this.zoomWindowContainerElementRef,
      'backgroundPosition',
      `${backgroundPositionX} ${backgroundPositionY}`
    );
    this.renderer.setStyle(this.zoomWindowContainerElementRef, 'display', 'block');
    this.renderer.setStyle(this.lensElementRef, 'display', 'block');
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    this.renderer.setStyle(this.zoomWindowContainerElementRef, 'display', 'none');
    this.renderer.setStyle(this.lensElementRef, 'display', 'none');
  }

  private createZoomElements() {
    const container = this.el.nativeElement;

    // Create and style lens
    this.lensElementRef = this.renderer.createElement('div');
    this.renderer.addClass(this.lensElementRef, 'lens');
    this.renderer.setStyle(this.lensElementRef, 'position', 'absolute');
    this.renderer.setStyle(this.lensElementRef, 'border', '1px solid #d4d4d4');
    this.renderer.setStyle(this.lensElementRef, 'background', 'rgba(0,0,0,0.5)');
    this.renderer.setStyle(this.lensElementRef, 'pointerEvents', 'none');
    this.renderer.setStyle(this.lensElementRef, 'display', 'none');
    this.renderer.appendChild(container, this.lensElementRef);

    // Create and style zoom window
    this.zoomWindowContainerElementRef = this.renderer.createElement('div');
    this.renderer.addClass(this.zoomWindowContainerElementRef, 'zoom-window-container');
    this.renderer.setStyle(this.zoomWindowContainerElementRef, 'position', 'absolute');
    this.renderer.setStyle(this.zoomWindowContainerElementRef, 'border', '1px solid #d4d4d4');
    this.renderer.setStyle(this.zoomWindowContainerElementRef, 'backgroundRepeat', 'no-repeat');
    this.renderer.setStyle(this.zoomWindowContainerElementRef, 'backgroundColor', '#fff');
    this.renderer.setStyle(this.zoomWindowContainerElementRef, 'display', 'none');
    this.renderer.setStyle(this.zoomWindowContainerElementRef, 'overflow', 'hidden');
    this.renderer.setStyle(this.zoomWindowContainerElementRef, 'zIndex', '10000');
    this.renderer.setStyle(this.zoomWindowContainerElementRef, 'boxShadow', 'rgba(0, 0, 0, 0.25) 0px 6px 8px 2px');
    this.renderer.setStyle(this.zoomWindowContainerElementRef, 'top', '0');
    this.renderer.setStyle(this.zoomWindowContainerElementRef, 'left', 'calc(100% + 20px)');

    const zoomWindowElementRef = this.zoomWindowElementRef();
    if (zoomWindowElementRef) {
      this.renderer.appendChild(zoomWindowElementRef, this.zoomWindowContainerElementRef);
    } else {
      this.renderer.appendChild(container, this.zoomWindowContainerElementRef);
    }

    // Set the image element
    this.imageElement = this.el.nativeElement.querySelector('img');
    if (this.imageElement) {
      this.updateZoomWindowImage();
    }
  }

  private updateLensAndZoomWindowSize() {
    if (this.imageElement) {
      const imageRect = this.imageElement.getBoundingClientRect();
      const lensWidth = imageRect.width * 0.51;
      const lensHeight = imageRect.height * 0.33;

      this.renderer.setStyle(this.lensElementRef, 'width', `${lensWidth}px`);
      this.renderer.setStyle(this.lensElementRef, 'height', `${lensHeight}px`);
      this.renderer.setStyle(this.lensElementRef, 'backgroundImage', `url('/assets/img/lenseImage.gif')`);
      const zoomRatio = this.zoomRatio();
      this.renderer.setStyle(this.zoomWindowContainerElementRef, 'width', `${lensWidth * zoomRatio}px`);
      this.renderer.setStyle(this.zoomWindowContainerElementRef, 'height', `${lensHeight * zoomRatio}px`);
    }
  }

  private updateZoomWindowImage() {
    const imgSrc = this.imgSrc();
    if (this.zoomWindowContainerElementRef && imgSrc) {
      this.renderer.setStyle(this.zoomWindowContainerElementRef, 'backgroundImage', `url(${imgSrc})`);
    }
  }
}
